/**
 * One-time secret display — the plaintext agent token.
 *
 * The server hashes the token and never returns it again, so this block is
 * the only chance the operator gets to copy it. It says so, and it stays put
 * until the surrounding modal/drawer is closed.
 */
import { useState } from "react";

interface Props {
  secret: string;
  label?: string;
  hint?: string;
}

export function SecretOnce({
  secret,
  label = "Token",
  hint = "Copy it now — it is stored hashed and will never be shown again.",
}: Props) {
  const [copied, setCopied] = useState(false);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(secret);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard blocked (insecure context / permissions) — the token is
      // selectable in the block below, so this is not an error state.
      setCopied(false);
    }
  };

  return (
    <div className="panel panel--flush">
      <div className="panel-header">
        <h3>{label}</h3>
        <button type="button" className="btn btn-second btn-sm" onClick={copy}>
          {copied ? "Copied" : "Copy"}
        </button>
      </div>
      <div className="panel-body space-y-2">
        <pre className="args-json" style={{ whiteSpace: "pre-wrap", wordBreak: "break-all" }}>
          <code>{secret}</code>
        </pre>
        <p className="text-xs text-danger">
          {hint}
        </p>
      </div>
    </div>
  );
}
